// utils/useEncryption.js
// Thin composable wrapper around CryptoJS so components can encrypt/decrypt
// arbitrary values using the same key as cookieUtils.
import CryptoJS from "crypto-js";

export const useEncryption = () => {
  const secretKey = process.env.VUE_APP_ENCRYPTION_KEY;
  const isEncryptionEnabled =
    typeof secretKey === "string" && secretKey.length > 0;

  /**
   * AES-encrypt any value.
   * Objects / arrays are JSON-stringified automatically.
   *
   * @param {any} data
   * @returns {string|any} Cipher string, or the original value on failure.
   */
  const encrypt = (data) => {
    try {
      if (!isEncryptionEnabled || data === null || data === undefined) {
        return data;
      }
      const stringData = typeof data === "string" ? data : JSON.stringify(data);
      return CryptoJS.AES.encrypt(stringData, secretKey).toString();
    } catch (error) {
      console.error("Encryption failed:", error);
      return data; // fallback to original
    }
  };

  /**
   * Decrypt a cipher string produced by encrypt().
   * Attempts JSON.parse on the plaintext; returns the string if parsing fails.
   *
   * @param {string} encryptedData
   * @returns {any} Decrypted value, or the original input on failure.
   */
  const decrypt = (encryptedData) => {
    try {
      if (!isEncryptionEnabled || !encryptedData) {
        return encryptedData;
      }
      const bytes = CryptoJS.AES.decrypt(encryptedData, secretKey);
      const decrypted = bytes.toString(CryptoJS.enc.Utf8);

      if (!decrypted) {
        // Wrong key or corrupted data — fall back to the raw value
        return encryptedData;
      }

      try {
        return JSON.parse(decrypted);
      } catch {
        return decrypted;
      }
    } catch (error) {
      console.error("[useEncryption] Decryption failed:", error);
      return encryptedData;
    }
  };

  return { encrypt, decrypt };
};
